import React, { useState, useRef, useEffect } from 'react';
import {
  Typography,
  TextField,
  Button,
  Container,
  Box,
  InputAdornment,
  Paper,
  Grid,
} from '@mui/material';
import QRCode from 'qrcode.react';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PhoneIcon from '@mui/icons-material/Phone';
import MessageIcon from '@mui/icons-material/Message';

function QRCodeGenerator() {
  const [formData, setFormData] = useState({
    phoneNumber: '',
    message: '',
  });
  const [qrCode, setQRCode] = useState('');
  const qrRef = useRef(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (formData.phoneNumber) {
      generateQRCode();
    } else {
      setQRCode('');
    }
  }, [formData]);

  const generateQRCode = () => {
    const url = `https://wa.me/${formData.phoneNumber}?text=${encodeURIComponent(formData.message)}`;
    setQRCode(url);
  };

  const handleDownload = () => {
    const canvas = qrRef.current.querySelector('canvas');
    const image = canvas.toDataURL("image/png");
    const link = document.createElement('a');
    link.href = image;
    link.download = `WhatsApp-QR-${formData.phoneNumber}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(qrCode);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, px: { xs: 1, sm: 2, md: 3 }, width: '100%', maxWidth: '1600px' }}>
      <Typography variant="h4" gutterBottom align="left" sx={{ fontWeight: 'bold', mb: 1 }}>
        WhatsApp QR Code Generator
      </Typography>
      <Typography sx={{ mb: 2 }}>
        The WhatsApp QR Code Generator is a user-friendly tool designed to create custom QR codes for WhatsApp conversations. Simply enter your phone number and an optional message, and the tool will generate a QR code that others can scan to start a chat with you on WhatsApp.
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Input
            </Typography>
            <TextField
              fullWidth
              label="Phone Number"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Message (Optional)"
              name="message"
              value={formData.message}
              onChange={handleChange}
              margin="normal"
              multiline
              rows={6}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MessageIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              QR Code Preview
            </Typography>
            <Box ref={qrRef} sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              {qrCode ? (
                <QRCode value={qrCode} size={200} level="H" />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Enter a phone number to generate QR code
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<CloudDownloadIcon />}
                onClick={handleDownload}
                disabled={!qrCode}
              >
                Download
              </Button>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopy}
                disabled={!qrCode}
              >
                Copy Link
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          How to Use This QR Code?
        </Typography>
        <Typography variant="body2" paragraph>
          1. Generate your QR code by entering your phone number (and optional message) above.<br />
          2. Download the QR code or copy the link.<br />
          3. Share the QR code in your marketing materials, business cards, or online profiles.<br />
          4. When someone scans the QR code, it will open a chat with you in WhatsApp.
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          Note: Make sure to use your full phone number including the country code.
        </Typography>
      </Paper>
    </Container>
  );
}

export default QRCodeGenerator;